import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from 'antd';

import LoadingSpinner from 'components/LoadingSpinner';

import { setLoadingAttachmentId } from 'features/commons/commonsSlice';

import useErrorCatch from 'hooks/useErrorCatch';
import useFileLoader from 'hooks/useFileLoader';
import { useAppDispatch } from 'hooks/useTypedRedux';

import fetchTextWithAuth from 'utils/api/fetchTextWithAuth';
import isS3SignedUrl from 'utils/api/isS3SignedUrl';
import normalizeUrlEndpoint from 'utils/api/normalizeUrlEndpoint';
import { isAttachmentToDownload } from 'utils/isAttachmentToDownload';

import { AttachmentTypes } from 'types/api';
import { Attachment } from 'types/commons';

import { RESOURCE } from 'constants/endpoints';
import { MimeTypes, SpinnerSizes } from 'constants/enums';

import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';

import styles from './FileViewModal.module.scss';
import adjustIframeImages from './utils/adjustIframeImages';
import { isImageFile } from './utils/checkFileType';
import downloadFile from './utils/downloadFile';
import generateFileName from './utils/generateFileName';

const MODAL_WIDTH = 900;

interface FileViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  attachment?: Attachment<AttachmentTypes> | null;
}

const FileViewModal: FC<FileViewModalProps> = ({
  isOpen,
  onClose,
  title,
  attachment
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const catchError = useErrorCatch();
  const ref = useRef<HTMLIFrameElement>(null);
  const [resolvedFileUrl, setResolvedFileUrl] = useState<string>('');

  const {
    data,
    isLoading,
    loadFile,
    fileType: downloadedFileType
  } = useFileLoader(resolvedFileUrl, onClose);

  useEffect(() => {
    if (!attachment) {
      return;
    }

    const resolveSignedUrl = async () => {
      const rawUrl = (attachment.url || '').trim();

      if (rawUrl && isS3SignedUrl(rawUrl)) {
        setResolvedFileUrl(rawUrl);
        return;
      }

      // Fallback for legacy data where url may be absent.
      // TODO: remove this fallback when we have a proper url in the attachment.
      const endpoint = rawUrl || `${RESOURCE}/${attachment.id}/url`;
      const signedUrl = await fetchTextWithAuth(normalizeUrlEndpoint(endpoint));
      setResolvedFileUrl(signedUrl);
    };

    resolveSignedUrl().catch(() => setResolvedFileUrl(''));
  }, [attachment]);

  useEffect(() => {
    if (!resolvedFileUrl) {
      return;
    }
    loadFile();
  }, [resolvedFileUrl, loadFile]);

  const isFileToDownload = attachment && isAttachmentToDownload(attachment);

  // Download unsupported files
  useEffect(() => {
    if (!isFileToDownload || !attachment) {
      return;
    }

    // For S3 signed URLs download directly without blob to avoid CORS.
    if (resolvedFileUrl && isS3SignedUrl(resolvedFileUrl)) {
      downloadFile({
        catchError,
        dataUrl: resolvedFileUrl,
        translation: t,
        name:
          attachment.filename ||
          generateFileName(resolvedFileUrl, attachment.fileType as MimeTypes)
      });
      onClose();
      return;
    }

    if (!data) {
      return;
    }

    downloadFile({
      catchError,
      dataUrl: data,
      translation: t,
      name:
        attachment.filename ||
        generateFileName(
          resolvedFileUrl || attachment.id,
          attachment.fileType as MimeTypes
        )
    });

    onClose();
  }, [
    isFileToDownload,
    data,
    attachment,
    resolvedFileUrl,
    catchError,
    t,
    onClose
  ]);

  const isImageUrl = (url: string | undefined | null): boolean => {
    const v = (url || '').toLowerCase();
    return v.endsWith('.png') || v.endsWith('.jpg') || v.endsWith('.jpeg');
  };

  const handleLoadIframe = () => {
    const iframe = ref.current;

    if (!iframe || !isImageFile(downloadedFileType)) {
      return;
    }
    adjustIframeImages(iframe);
  };

  const canOpenInModal = useMemo(
    () => isOpen && !isFileToDownload,
    [isOpen, isFileToDownload]
  );

  const isShowData = !isLoading && data;
  const isImage =
    isImageFile(downloadedFileType) ||
    isImageUrl(attachment?.filename) ||
    isImageUrl(resolvedFileUrl);

  useEffect(() => {
    if (!isFileToDownload || !attachment?.id) {
      return;
    }

    // We need this to propagate downloading state to eye icon for files, we are not going to preview
    dispatch(setLoadingAttachmentId(isLoading ? attachment.id : null));
  }, [isFileToDownload, attachment?.id, isLoading, dispatch]);

  return (
    <>
      {canOpenInModal && (
        <Modal
          open
          onCancel={onClose}
          footer={null}
          title={title}
          closeIcon={<CloseIcon />}
          destroyOnHidden
          width={MODAL_WIDTH}
        >
          {isLoading && (
            <div className={styles.spinner}>
              <LoadingSpinner size={SpinnerSizes.Large} isFullHeight={false} />
            </div>
          )}
          {isShowData && (
            <>
              {isImage ? (
                <div className={styles.imageContainer}>
                  <img
                    src={data}
                    alt={title || 'File preview'}
                    className={styles.image}
                  />
                </div>
              ) : (
                <iframe
                  onLoad={handleLoadIframe}
                  src={data}
                  className={styles.frame}
                  ref={ref}
                  title={title || 'File preview'}
                />
              )}
            </>
          )}
        </Modal>
      )}
    </>
  );
};

export default FileViewModal;
